// Shared helpers for the views: server config, device name, screen wake lock, save & share.

import qrcode from 'qrcode-generator';

let iceCache: { at: number; servers: RTCIceServer[] } | null = null;

/** STUN/TURN servers; TURN credentials live 1 h, refreshed well before that. */
export async function iceServers(): Promise<RTCIceServer[]> {
  if (iceCache && Date.now() - iceCache.at < 45 * 60_000) return iceCache.servers;
  const res = await fetch('/api/ice', { cache: 'no-store' });
  const { iceServers: servers } = (await res.json()) as { iceServers: RTCIceServer[] };
  iceCache = { at: Date.now(), servers };
  return servers;
}

export function wsUrl(path: string): string {
  return `${location.origin.replace(/^http/, 'ws')}${path}`;
}

const ANIMALS = ['Loutre', 'Renard', 'Hibou', 'Lynx', 'Panda', 'Koala', 'Héron', 'Castor', 'Faucon', 'Tortue', 'Dauphin', 'Ours', 'Lièvre', 'Mésange', 'Chamois', 'Blaireau'];
const COLORS = ['ambre', 'azur', 'corail', 'jade', 'ocre', 'olive', 'prune', 'safran', 'sable', 'indigo', 'cobalt', 'ivoire', 'lilas', 'cuivre', 'émeraude', 'céladon'];
const NAME_KEY = 'drop:name';
let fallbackName: string | null = null;

function pick<T>(list: T[]): T {
  return list[crypto.getRandomValues(new Uint32Array(1))[0] % list.length];
}

export function randomDeviceName(): string {
  return `${pick(ANIMALS)} ${pick(COLORS)}`;
}

/** This device's display name, kept across visits (storage may be unavailable: then per visit). */
export function deviceName(): string {
  try {
    let name = localStorage.getItem(NAME_KEY);
    if (!name) {
      name = randomDeviceName();
      localStorage.setItem(NAME_KEY, name);
    }
    return name;
  } catch {
    return (fallbackName ??= randomDeviceName());
  }
}

export function setDeviceName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // Not persisted; the next visit gets a random name again.
  }
}

/** Keeps the screen on while something runs (iOS 16.4+, Android, desktop). Re-acquired on return. */
export function keepAwake(): () => void {
  let lock: { release(): Promise<void> } | null = null;
  let active = true;
  const acquire = async () => {
    try {
      lock = await (navigator as unknown as { wakeLock?: { request(t: 'screen'): Promise<{ release(): Promise<void> }> } }).wakeLock?.request('screen') ?? null;
    } catch {
      lock = null;
    }
  };
  const onVisible = () => active && document.visibilityState === 'visible' && void acquire();
  document.addEventListener('visibilitychange', onVisible);
  void acquire();
  return () => {
    active = false;
    document.removeEventListener('visibilitychange', onVisible);
    void lock?.release().catch(() => {});
  };
}

export function saveFile(file: File): void {
  const url = URL.createObjectURL(file);
  const a = Object.assign(document.createElement('a'), { href: url, download: file.name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function canShareFiles(files: File[]): boolean {
  try {
    return !!navigator.canShare?.({ files });
  } catch {
    return false;
  }
}

/** System share sheet: on iOS this is how a photo or video lands in the gallery. */
export async function shareFiles(files: File[]): Promise<void> {
  try {
    await navigator.share({ files });
  } catch (e) {
    if ((e as Error).name !== 'AbortError') throw e;
  }
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** QR code as an SVG element (generated locally from our own link, never from peer input). */
export function qrElement(text: string): HTMLElement {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  const box = document.createElement('div');
  box.className = 'qr';
  box.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
  return box;
}
